/**
 * EventLocationPickerModal — minimal MapLibre map in a modal that lets the
 * user click or drag to set a precise latitude/longitude for an event.
 * Deliberately renders no other businesses/properties/events so the picker
 * stays focused on the single pin being placed.
 */

import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheck,
  faLocationCrosshairs,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { ISLAND_CENTROIDS, USVI_BOUNDS, resolveIslandKey } from "../../constants/usviGeo";

export default function EventLocationPickerModal({
  initialLat,
  initialLng,
  island,
  onCancel,
  onConfirm,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [pin, setPin] = useState(() => {
    // Reject null/undefined/"" before Number() — `Number(null)` is 0,
    // which would otherwise pass Number.isFinite and drop a pin at
    // (0, 0) in the middle of the Atlantic.
    if (initialLat == null || initialLat === "" || initialLng == null || initialLng === "") return null;
    const lat = Number(initialLat);
    const lng = Number(initialLng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) return { lat, lng };
    return null;
  });

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const islandKey = resolveIslandKey(island);
    const startCenter = pin
      ? [pin.lng, pin.lat]
      : islandKey && ISLAND_CENTROIDS[islandKey]
        ? [ISLAND_CENTROIDS[islandKey].lng, ISLAND_CENTROIDS[islandKey].lat]
        : [USVI_BOUNDS.center[0], USVI_BOUNDS.center[1]];

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: [
              "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
              "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
            ],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: startCenter,
      zoom: pin ? 15 : 12,
      minZoom: 8,
      maxZoom: 18,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    const placeMarker = (lng, lat) => {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const m = new maplibregl.Marker({ color: "#b88a3d", draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);
        m.on("dragend", () => {
          const ll = m.getLngLat();
          setPin({ lat: ll.lat, lng: ll.lng });
        });
        markerRef.current = m;
      }
    };

    if (pin) placeMarker(pin.lng, pin.lat);

    map.on("click", (e) => {
      const { lng, lat } = e.lngLat;
      setPin({ lat, lng });
      placeMarker(lng, lat);
    });

    mapRef.current = map;

    // The modal animates in and the flex container's height isn't necessarily
    // measured by the time MapLibre takes its first canvas size. Without
    // explicit resize calls the map ends up 0×0 (or stuck at the initial
    // mount-time dimensions) and tiles never load — you just see the empty
    // map background colour. Resize on the next frame, on `load`, and again
    // whenever the container itself changes size.
    const resize = () => map.resize();
    map.on("load", resize);
    const raf = window.requestAnimationFrame(resize);
    const t1 = window.setTimeout(resize, 80);
    const t2 = window.setTimeout(resize, 300);
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      ro = new ResizeObserver(resize);
      ro.observe(containerRef.current);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      if (ro) ro.disconnect();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync programmatic pin updates (Recenter / Clear buttons).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pin) {
      if (markerRef.current) {
        markerRef.current.setLngLat([pin.lng, pin.lat]);
      } else {
        const m = new maplibregl.Marker({ color: "#b88a3d", draggable: true })
          .setLngLat([pin.lng, pin.lat])
          .addTo(map);
        m.on("dragend", () => {
          const ll = m.getLngLat();
          setPin({ lat: ll.lat, lng: ll.lng });
        });
        markerRef.current = m;
      }
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [pin]);

  const recenterOnIsland = () => {
    const key = resolveIslandKey(island);
    if (!key || !ISLAND_CENTROIDS[key]) return;
    const { lat, lng } = ISLAND_CENTROIDS[key];
    setPin({ lat, lng });
    mapRef.current?.easeTo({ center: [lng, lat], zoom: 13, duration: 400 });
  };

  const clearPin = () => setPin(null);

  return (
    <div className="event-loc-picker__backdrop" role="dialog" aria-modal="true" aria-label="Pick event location on the map">
      <div className="event-loc-picker__modal">
        <div className="event-loc-picker__header">
          <h3>Pick a point on the map</h3>
          <button type="button" className="event-loc-picker__close" onClick={onCancel} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <p className="event-loc-picker__hint">
          Click anywhere on the map to drop a pin, or drag the pin to fine-tune
          the location. The map only shows base imagery — no other Guild markers.
        </p>
        <div className="event-loc-picker__map" ref={containerRef} />
        <div className="event-loc-picker__footer">
          <div className="event-loc-picker__coords">
            {pin ? (
              <>
                <strong>Pin:</strong> {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
              </>
            ) : (
              <em>No pin yet — click the map to drop one.</em>
            )}
          </div>
          <div className="event-loc-picker__buttons">
            <button
              type="button"
              className="btn-secondary"
              onClick={recenterOnIsland}
              disabled={!island}
              title={island ? "Recenter on this island" : "Pick an island first"}
            >
              <FontAwesomeIcon icon={faLocationCrosshairs} /> Recenter on island
            </button>
            {pin && (
              <button type="button" className="btn-secondary" onClick={clearPin}>
                <FontAwesomeIcon icon={faCircleXmark} /> Clear pin
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!pin}
              onClick={() => pin && onConfirm(pin)}
            >
              <FontAwesomeIcon icon={faCheck} /> Use this location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
